/* eslint-disable indent */
import NetworkError from './NetworkError';
import { getCookie } from "../modules/common/utils/cookiesUtils.js";
import { enqueueRequest } from './requestQueue';

const SERVICE_TOKEN_NAME = 'serviceToken';

const DEFAULT_TIMEOUT_MS = 10000;

let networkErrorCallback;
let reauthenticationCallback;

const isJson = (response) => {
  const contentType = response.headers.get('content-type');
  return contentType && contentType.indexOf('application/json') !== -1;
};

const handleOkResponse = (response, onSuccess) => {
  if (!response.ok) {
    return false;
  }
  if (!onSuccess) {
    return true;
  }
  if (response.status === 204) {
    onSuccess();
    return true;
  }
  if (isJson(response)) {
    response.json().then((payload) => onSuccess(payload));
  } else {
    onSuccess();
  }

  return true;
};

const handle4xxResponse = (response, onErrors) => {
  if (response.status < 400 || response.status >= 500) {
    return false;
  }
  if (response.status === 401 && reauthenticationCallback) {
    reauthenticationCallback();
    return true;
  }
  if (!isJson(response)) {
    throw new NetworkError();
  }
  if (onErrors) {
    response.json().then((payload) => {
      if (payload.code || payload.fieldErrors) {
        onErrors(payload);
      } else {
        onErrors('Fatal Error');
      }
    });
  }

  return true;
};

const handleResponse = (response, onSuccess, onErrors) => {
  if (handleOkResponse(response, onSuccess)) {
    return;
  }
  if (handle4xxResponse(response, onErrors)) {
    return;
  }
  throw new NetworkError();
};

export const init = (callback) => (networkErrorCallback = callback);

export const setReauthenticationCallback = (callback) =>
  (reauthenticationCallback = callback);


export const getServiceToken = () => getCookie(SERVICE_TOKEN_NAME);

export const config = (method, body) => {
  const config = {};
  config.method = method;
  if (body) {
    if (body instanceof FormData) {
      config.body = body;
    } else {
      config.headers = {'Content-Type': 'application/json'};
      config.body = JSON.stringify(body);
    }
  }
  const serviceToken = getServiceToken();
  if (serviceToken) {
    if (config.headers) {
      config.headers['Authorization'] = `Bearer ${serviceToken}`;
    } else {
      config.headers = {Authorization: `Bearer ${serviceToken}`};
    }
  }
  
  return config;
};

// Por defecto solo encolamos y abortamos las lecturas (GET). Abortar un POST en
// cliente NO cancela lo que el servidor ya esté haciendo: en un pago o una reserva de
// butaca dejaría al usuario sin respuesta con la operación posiblemente ya ejecutada.
const resolveRequestOptions = (options, requestOptions) => {
  const isRead = ((options && options.method) || 'GET').toUpperCase() === 'GET';
  const overrides = requestOptions || {};

  return {
    timeoutMs: overrides.timeoutMs !== undefined
      ? overrides.timeoutMs
      : (isRead ? DEFAULT_TIMEOUT_MS : null),
    queue: overrides.queue !== undefined ? overrides.queue : isRead,
  };
};

const fetchWithTimeout = (path, options, timeoutMs) => {
  if (!timeoutMs || typeof AbortController === 'undefined') {
    return fetch(path, options);
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(path, {...options, signal: controller.signal}).finally(() =>
    clearTimeout(timer)
  );
};

// Nunca dejamos que un fallo se pierda en un `.catch(undefined)`: si nadie ha
// registrado `networkErrorCallback` con `init()`, al menos lo dejamos en consola.
const handleNetworkError = (path, error, onErrors) => {
  const isTimeout = error && error.name === 'AbortError';
  console.warn(`[appFetch] ${isTimeout ? 'Timeout' : 'Error de red'}: ${path}`, error);

  if (typeof onErrors === 'function') {
    onErrors(error);
    return;
  }
  if (typeof networkErrorCallback === 'function') {
    networkErrorCallback(error);
  }
};

const runFetch = (path, options, onSuccess, onErrors, timeoutMs, releaseSlot) =>
  fetchWithTimeout(path, options, timeoutMs)
    // El slot se libera en cuanto contesta la red, sin depender de que los callbacks
    // lleguen a invocarse (la rama de reautenticación, por ejemplo, no llama a ninguno).
    .finally(releaseSlot)
    .then((response) => {
      try {
        handleResponse(response, onSuccess, onErrors);
      } catch (error) {
        handleNetworkError(path, error, onErrors);
      }
    })
    .catch((error) => handleNetworkError(path, error, onErrors));

const noop = () => {};

export const appFetch = (path, options, onSuccess, onErrors, requestOptions) => {
  const {timeoutMs, queue} = resolveRequestOptions(options, requestOptions);

  if (!queue) {
    runFetch(path, options, onSuccess, onErrors, timeoutMs, noop);
    return;
  }
  enqueueRequest((done) => runFetch(path, options, onSuccess, onErrors, timeoutMs, done));
};

export const appFetchFile = (path, options, onSuccess, onErrors) => {
  const formData = new FormData();
  formData.append('document', options.file, options.file.name);
  formData.append('data', JSON.stringify(options.data));
  formData.append('token', options.token);
  fetch(path, {
    method: 'POST',
    body: formData
  }).then((response) => {
    try {
      handleResponse(response, onSuccess, onErrors);
    } catch (error) {
      handleNetworkError(path, error, onErrors);
    }
  }).catch((error) => handleNetworkError(path, error, onErrors));
}
